import { makeAutoObservable, runInAction } from "mobx";
import { CraNodeApi } from "../services/CraNodeApi";
import { RootStore } from "../../shared/stores/RootStore";
import { CraNode } from "../models/CraNode";
import { OptionalScope } from "../../shared/models/OptionalScope";
import { CraWayApi } from "../services/CraWayApi";
import { MapboxApi } from "../../map/services/MapboxApi";

export const defaultNodeScopes = [
  OptionalScope.Score,
  OptionalScope.SafetyTreatmentModel,
  OptionalScope.Photo,
  OptionalScope.ScoreImpact,
  OptionalScope.CodingSuggestions,
];

interface NodeReference {
  craWayId: string;
  index: number;
}

type craWayId = string;
type craNodeId = string;

export const nodeReferences = new Map<string, NodeReference>();

export class CraNodeStore {
  rootStore;
  nodesByWayId = new Map<craWayId, CraNode[]>();
  selectedId?: craNodeId;
  multiSelectedIds = new Set<craNodeId>();

  private nodeRefreshTimeout?: NodeJS.Timeout;
  showNodeRefreshAlert = false;

  /** data collection stage */
  pendingCoordsByNodeId = new Map<craNodeId, number[]>();

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  resetState() {
    runInAction(() => {
      this.nodesByWayId = new Map();
      this.selectedId = undefined;

      this.showNodeRefreshAlert = false;
      if (this.nodeRefreshTimeout) clearInterval(this.nodeRefreshTimeout);
      this.nodeRefreshTimeout = undefined;
    });
  }

  private getFrom(nodeId: string, source: Map<string, CraNode[]>) {
    const reference = nodeReferences.get(nodeId);
    if (reference) {
      return source.get(reference.craWayId)?.[reference.index];
    }
    return;
  }

  /** will get the forecasted version if available */
  get(nodeId: string) {
    const { filterStore } = this.rootStore;
    return this.getFrom(nodeId, filterStore.originalOrForecastedNodes);
  }

  /** will always get the non-forecasted version */
  getOriginal(nodeId: string) {
    const { filterStore } = this.rootStore;
    return this.getFrom(nodeId, filterStore.nodesAvailableInCurrentMode);
  }

  getSelected() {
    if (!this.selectedId) return;
    return this.get(this.selectedId);
  }

  select(nodeId: string) {
    runInAction(() => (this.selectedId = nodeId));

    this.updateCraMapSource();
  }

  setMultiSelectedIds(value: Set<string>) {
    runInAction(() => (this.multiSelectedIds = value));

    this.updateCraMapSource();
  }

  updateCraMapSource() {
    const { mapStore } = this.rootStore;
    mapStore.craMapSource?.onStoreUpdate();
  }

  toggleMultiSelectedId(nodeId: string) {
    const newSet = new Set(this.multiSelectedIds);
    if (newSet.has(nodeId)) {
      newSet.delete(nodeId);
    } else {
      newSet.add(nodeId);
    }
    this.setMultiSelectedIds(newSet);
  }

  getMultiSelectedNodesByCraWay(craWayId: string): CraNode[] {
    const nodes: CraNode[] = [];
    for (const nodeId of this.multiSelectedIds) {
      const node = this.get(nodeId);
      if (node?.craWayId === craWayId) {
        nodes.push(node);
      }
    }
    return nodes;
  }

  async matchRoute(nodes: CraNode[]) {
    const chunkSize = 100;

    for (let i = 0; i < nodes.length; i += chunkSize) {
      const nodesChunk = nodes.slice(i, i + chunkSize);
      const coordString = nodesChunk
        .map((node) => `${node.longitude},${node.latitude}`)
        .join(";");

      await MapboxApi.mapMatching(coordString).then((tracepoints) => {
        tracepoints.forEach((tracepoint, index) => {
          if (tracepoint) {
            const matchedNode = nodesChunk[index];
            this.pendingCoordsByNodeId.set(matchedNode.id, tracepoint.location);
          }
        });
      });
    }

    runInAction(() => {
      this.pendingCoordsByNodeId = new Map(this.pendingCoordsByNodeId);
    });
    this.updatePendingCoordChangesMapSource();
  }

  setNodeCoords(nodeId: string, lat: number, lng: number) {
    runInAction(() => {
      this.pendingCoordsByNodeId.set(nodeId, [lng, lat]);
      this.pendingCoordsByNodeId = new Map(this.pendingCoordsByNodeId);
    });

    this.updateCraMapSource();
    this.updatePendingCoordChangesMapSource();
  }

  updatePendingCoordChangesMapSource() {
    const { mapStore } = this.rootStore;
    mapStore.pendingCoordChangesMapSource?.onStoreUpdate();
  }

  clearPendingCoordChanges() {
    runInAction(() => {
      this.pendingCoordsByNodeId = new Map();
    });

    this.updateCraMapSource();
    this.updatePendingCoordChangesMapSource();
  }

  async applyPendingCoordChanges(craWayId: string) {
    await CraWayApi.batchUpdateLocation(
      craWayId,
      [...this.pendingCoordsByNodeId].map(([nodeId, coords]) => ({
        craNodeId: nodeId,
        longitude: coords[0],
        latitude: coords[1],
      })),
    );
  }

  clearSelected() {
    runInAction(() => {
      this.selectedId = undefined;
      this.multiSelectedIds = new Set();
    });
  }

  async deleteSelectedNodes(onDelete?: (nodeId: string) => void) {
    const newNodesByWayId = new Map(this.nodesByWayId);

    for (const nodeId of this.multiSelectedIds.values()) {
      await CraNodeApi.delete(nodeId);
      onDelete?.(nodeId);

      const reference = nodeReferences.get(nodeId)!;
      newNodesByWayId.set(
        reference.craWayId,
        newNodesByWayId
          .get(reference.craWayId)!
          .filter((node) => node.id !== nodeId),
      );
    }

    runInAction(() => {
      this.nodesByWayId = newNodesByWayId;

      // select the node that replaces the selectedId in the route's order
      if (this.selectedId && this.multiSelectedIds.has(this.selectedId)) {
        const previouslySelectedReference = nodeReferences.get(
          this.selectedId,
        )!;
        const newNodeSelection = newNodesByWayId.get(
          previouslySelectedReference.craWayId,
        )![previouslySelectedReference.index];
        if (newNodeSelection) {
          this.select(newNodeSelection.id);
        }
      }
      this.rebuildNodeReferences();
      this.multiSelectedIds = new Set();
    });
  }

  async flipSelectedNodes(onDone?: (nodeId: string) => void) {
    for (const nodeId of this.multiSelectedIds.values()) {
      const newUrl = (await CraNodeApi.rotate(nodeId, { degrees: 180 })).data;
      onDone?.(nodeId);

      // update the signed url in memory to refresh the images
      const reference = nodeReferences.get(nodeId)!;
      const nodes = this.nodesByWayId.get(reference.craWayId)!;
      const node = nodes[reference.index];
      node.photo!.url = newUrl;
      nodes[reference.index] = { ...node };
    }
  }

  rebuildNodeReferences() {
    for (const [craWayId, nodes] of this.nodesByWayId) {
      nodes.forEach((node, index) => {
        nodeReferences.set(node.id, { index, craWayId });
      });
    }
  }

  async fetchNodes(
    craWayId: string,
    scopes: OptionalScope[] = defaultNodeScopes,
    cancelIf?: () => boolean,
  ) {
    await CraWayApi.compileAllNodePages(craWayId, scopes, 400).then((nodes) => {
      if (cancelIf?.()) return;

      runInAction(() => {
        this.nodesByWayId.set(craWayId, nodes);
        this.nodesByWayId = new Map(this.nodesByWayId);
      });
      nodes.forEach((node, index) => {
        nodeReferences.set(node.id, { craWayId, index });
      });
    });

    this.resetNodeRefreshTimeout();
  }

  async refreshNodeScore(nodeId: string) {
    const node = this.get(nodeId);
    if (node) {
      const response = await CraNodeApi.getScore(node.id!);

      runInAction(() => {
        node!.score = response.data || null;
        this.nodesByWayId = new Map(this.nodesByWayId);
      });
    }
  }

  private resetNodeRefreshTimeout = () => {
    const callback = () => {
      runInAction(() => {
        this.showNodeRefreshAlert = true;
        this.nodeRefreshTimeout = undefined;
      });
    };
    runInAction(() => {
      this.showNodeRefreshAlert = false;
      if (this.nodeRefreshTimeout) clearInterval(this.nodeRefreshTimeout);
      const ms = 1000 * 60 * 60; // 1 hour
      this.nodeRefreshTimeout = setTimeout(callback, ms);
    });
  };

  closeNodeRefreshAlert = () => {
    runInAction(() => {
      this.showNodeRefreshAlert = false;
    });
  };
}
